import * as React from 'react';
import {connect} from 'react-redux';

import {Field, formValueSelector, reset, change} from 'redux-form';

import type {CoreEntityType} from 'nutshell-core/types';
import {getSingularEntityType} from 'nutshell-core/utils/get-singular-entity-type';

import {nonNull} from 'shells/form/validation/non-null';
import {AccountIcon} from 'shells/icon/svg/account-icon';
import {ContactIcon} from 'shells/icon/svg/contact-icon';
import {LeadIcon} from 'shells/icon/svg/lead-icon';
import {ReduxFormSelectTwoLineIcon} from 'shells/redux-form-fields/select-two-line-icon/select-two-line-icon';
import {SpacerGroup} from 'shells/spacer/spacer-group';
import {Body} from 'shells/typography/body';

import type {CreateRulesFragment} from 'nutshell-graphql-types';

import {FORM_NAME} from './create-rules-constants';
import {CreateRulesFormContact, CreateRulesFormAccount, CreateRulesFormLead} from './forms';
import {canDeleteCreateRule} from './helpers';

import styles from './create-rules-form.module.css';

export const createRuleEntityValues = [
    {
        value: 'contacts',
        label: 'Create a person',
        sublabel: '',
        icon: ContactIcon,
    },
    {
        value: 'accounts',
        label: 'Create a company',
        sublabel: '',
        icon: AccountIcon,
    },
    {
        value: 'leads',
        label: 'Create a lead',
        sublabel: '',
        icon: LeadIcon,
    },
] as const;

const entityTypeSelector = formValueSelector(FORM_NAME);

// @ts-expect-error
function mapStateToProps(state): StateProps {
    return {
        entityType: entityTypeSelector(state, 'entityType'),
    };
}

// @ts-expect-error
function mapDispatchToProps(dispatch): DispatchProps {
    return {
        onEntityTypeChange: (newValue?: string | null) => {
            dispatch(reset(FORM_NAME));
            dispatch(change(FORM_NAME, 'entityType', newValue));
        },
    };
}

type OwnProps = {
    formId: string;
    createRules: CreateRulesFragment;
    configuredEntityTypes: CoreEntityType[];
    startFromEntityType: CoreEntityType | null | undefined;
    hasLeadSpecialCases: boolean;
};

type StateProps = {
    entityType?: CoreEntityType;
};

type DispatchProps = {
    onEntityTypeChange: (newValue?: CoreEntityType | null | undefined) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

export function CreateRulesFormComponent(props: Props) {
    const {entityType, onEntityTypeChange} = props;

    React.useEffect(() => {
        onEntityTypeChange(entityType);
    }, [entityType, onEntityTypeChange]);

    return (
        <div className={styles['create-rules-form']}>
            <div className={styles['form-description']}>
                {getFormDescription(props.createRules, props.startFromEntityType)}
            </div>
            <Body>When this form is submitted…</Body>
            {props.startFromEntityType ? (
                <SpacerGroup mb={24}>
                    <CreateRulesReadOnlyHeading entityType={props.startFromEntityType} />
                </SpacerGroup>
            ) : (
                <div style={{marginTop: 8}}>
                    <Field
                        name='entityType'
                        component={ReduxFormSelectTwoLineIcon}
                        options={createRuleEntityValues.filter((selectValues) => {
                            // This is filtering out entity types that already
                            // have create rules configured for them, so that
                            // they can't be selected when adding a _new_
                            // create rule
                            return !props.configuredEntityTypes.includes(selectValues.value);
                        })}
                        validate={[nonNull]}
                        placeholder='Select a starting point…'
                    />
                </div>
            )}
            {entityType === 'contacts' || props.startFromEntityType === 'contacts' ? (
                <CreateRulesFormContact />
            ) : undefined}
            {entityType === 'accounts' || props.startFromEntityType === 'accounts' ? (
                <CreateRulesFormAccount />
            ) : undefined}
            {entityType === 'leads' || props.startFromEntityType === 'leads' ? (
                <CreateRulesFormLead
                    formId={props.formId}
                    hasLeadSpecialCases={props.hasLeadSpecialCases}
                />
            ) : undefined}
        </div>
    );
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);
export const CreateRulesForm = connector(CreateRulesFormComponent);

function CreateRulesReadOnlyHeading({entityType}: {entityType: CoreEntityType}) {
    switch (entityType) {
        case 'contacts':
            return (
                <Body>
                    <strong>Create a person</strong>
                </Body>
            );
        case 'accounts':
            return (
                <Body>
                    <strong>Create a company</strong>
                </Body>
            );
        case 'leads':
            return (
                <Body>
                    <strong>Create a lead</strong>
                </Body>
            );
    }

    return null;
}

function getFormDescription(createRules: CreateRulesFragment, entityType?: CoreEntityType | null) {
    if (entityType && !canDeleteCreateRule(createRules, entityType)) {
        return (
            <p>
                This create rule cannot be removed, because this form collects{' '}
                <strong>{getSingularEntityType(entityType)}</strong> data that need to be stored in
                Nutshell.
            </p>
        );
    }

    return (
        <p>
            Add a rule that will automatically create a person, company, or lead in Nutshell when
            this form is submitted.
        </p>
    );
}
